import { Calendar, MapPin, Clock, Users, Bus, User, Phone, AlertCircle, ExternalLink, CreditCard, ChevronDown, ChevronUp, TrendingUp, Package2, CheckCircle2, ShoppingBag, HelpCircle, X, AlertTriangle, Timer, Shield, RefreshCw, FileText, MessageSquare, ArrowUpDown, SortAsc, Clock3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { useBookings, getBookingStatus, getBookingStatusText, getBookingStatusVariant, type BookingWithDetails } from "@/hooks/useBookings";
import { useAuth } from "@/hooks/useAuth";
import { canCancelBooking, canModifyBooking } from "@/hooks/useBookingDetail";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { AnimatedSkeleton } from "@/components/ui/animated-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { TRIP_STATUS } from "@/lib/trip-status";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
type SortOption = 'urgency' | 'departure' | 'booking' | 'status';

const BookingHistory = () => {
  const {
    isAuthenticated
  } = useAuth();
  const {
    data: bookings,
    isLoading,
    error
  } = useBookings();
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const [expandedCards, setExpandedCards] = useState<{
    [key: number]: boolean;
  }>({});
  const [sortOption, setSortOption] = useState<SortOption>(() => {
    const saved = localStorage.getItem('bookingSortOption');
    return (saved as SortOption) || 'urgency';
  });

  // 정렬 옵션 변경 핸들러
  const handleSortChange = (newSort: SortOption) => {
    setSortOption(newSort);
    localStorage.setItem('bookingSortOption', newSort);
  };

  // 출발까지 남은 일수 계산
  const getDaysUntilDeparture = (departureDate: string) => {
    const now = new Date();
    const departure = new Date(departureDate);
    const diffTime = departure.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 결제 대기 시간 계산 (24시간 중 남은 시간)
  const getHoursUntilPaymentDeadline = (createdAt: string) => {
    const created = new Date(createdAt);
    const deadline = new Date(created.getTime() + 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    return Math.max(0, diffHours);
  };

  // 긴급도 점수 계산
  const getUrgencyScore = (booking: BookingWithDetails) => {
    const status = getBookingStatus(booking);
    
    // 결제 대기 중
    if (status === 'pending') {
      const hoursLeft = getHoursUntilPaymentDeadline(booking.created_at);
      return 1000 - hoursLeft; // 시간이 적게 남을수록 높은 점수
    }
    
    // 예약 완료
    if (status === 'confirmed') {
      const daysUntil = getDaysUntilDeparture(booking.trip.departure_date);
      if (daysUntil <= 0) return 900; // 오늘 출발
      if (daysUntil <= 3) return 800 - daysUntil; // 3일 이내
      if (daysUntil <= 7) return 700 - daysUntil; // 7일 이내
      return 600 - Math.min(daysUntil, 100); // 그 이후
    }
    
    // 완료된 여행 (최근 30일)
    if (booking.trip?.status === TRIP_STATUS.COMPLETED) {
      const completedDate = new Date(booking.trip.departure_date);
      const daysSince = Math.floor((new Date().getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSince <= 30) return 400 - daysSince;
      return 300 - Math.min(daysSince, 100);
    }
    
    // 취소된 예약
    if (status === 'cancelled') {
      return 100;
    }
    
    return 0;
  };

  // 정렬된 예약 목록 반환
  const getSortedBookings = (bookings: BookingWithDetails[]) => {
    const sorted = [...bookings];
    
    switch (sortOption) {
      case 'urgency':
        return sorted.sort((a, b) => getUrgencyScore(b) - getUrgencyScore(a));
      case 'departure':
        return sorted.sort((a, b) => 
          new Date(a.trip.departure_date).getTime() - new Date(b.trip.departure_date).getTime()
        );
      case 'booking':
        return sorted.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case 'status':
        // 상태별 그룹 후 각 그룹 내 출발일 순
        return sorted.sort((a, b) => {
          const statusOrder = ['pending', 'confirmed', 'completed', 'cancelled'];
          const aStatus = getBookingStatus(a);
          const bStatus = getBookingStatus(b);
          const aCompleted = a.trip?.status === TRIP_STATUS.COMPLETED;
          const bCompleted = b.trip?.status === TRIP_STATUS.COMPLETED;
          
          const aIndex = aCompleted ? 2 : statusOrder.indexOf(aStatus);
          const bIndex = bCompleted ? 2 : statusOrder.indexOf(bStatus);
          
          if (aIndex !== bIndex) return aIndex - bIndex;
          return new Date(a.trip.departure_date).getTime() - new Date(b.trip.departure_date).getTime();
        });
      default:
        return sorted;
    }
  };

  // 예약을 상태별로 분류
  const categorizeBookings = (bookings: BookingWithDetails[]) => {
    const pending = bookings.filter(booking => getBookingStatus(booking) === 'pending');
    const confirmed = bookings.filter(booking => getBookingStatus(booking) === 'confirmed');
    // completed 상태는 trip의 상태로 판단 (trip.status === 'completed')
    const completed = bookings.filter(booking => booking.trip?.status === TRIP_STATUS.COMPLETED);
    const cancelled = bookings.filter(booking => getBookingStatus(booking) === 'cancelled');
    return {
      pending,
      confirmed,
      completed,
      cancelled
    };
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };
  const getLocationText = (booking: BookingWithDetails) => {
    const course = booking.trip.product.product_courses[0]?.course;
    if (course?.start_point && course?.end_point) {
      return `${course.start_point} → ${course.end_point}`;
    }
    return '상세 경로 정보 없음';
  };
  const getImageUrl = (booking: BookingWithDetails) => {
    const course = booking.trip.product.product_courses[0]?.course;
    return course?.main_photo_url || booking.trip.product.main_photo_url || '/images/image1.webp';
  };
  const toggleCardExpansion = (bookingId: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };
  const renderBookingCard = (booking: BookingWithDetails) => {
    const status = getBookingStatus(booking);
    const course = booking.trip.product.product_courses[0]?.course;
    const isExpanded = expandedCards[booking.booking_id] || false;
    const isCompletedTrip = booking.trip?.status === TRIP_STATUS.COMPLETED;
    const daysUntil = getDaysUntilDeparture(booking.trip.departure_date);
    const hoursUntilPayment = status === 'pending' ? getHoursUntilPaymentDeadline(booking.created_at) : 0;
    
    // 긴급도 레벨 결정
    const getUrgencyLevel = () => {
      if (status === 'pending' && hoursUntilPayment <= 6) return 'critical';
      if (status === 'pending') return 'warning';
      if (status === 'confirmed' && daysUntil <= 3) return 'warning';
      if (status === 'confirmed' && daysUntil <= 7) return 'info';
      return 'normal';
    };
    
    const urgencyLevel = getUrgencyLevel();
    
    return <Card key={booking.booking_id} className={cn("transition-all duration-200 hover:shadow-lg overflow-hidden group", status === 'confirmed' && "border-green-200 bg-green-50/30", status === 'pending' && "border-orange-200 bg-orange-50/30", status === 'cancelled' && "border-gray-200 opacity-75", isCompletedTrip && "border-gray-200")}>
        <CardContent className="p-0">
          <div className="flex flex-col md:flex-row">
            {/* 이미지 - 모바일에서는 상단에 크게 */}
            <div className={cn("overflow-hidden flex-shrink-0 relative", isMobile ? "w-full h-48" : "w-48 md:w-56 lg:w-64")}>
              {/* 긴급도 배지 */}
              {urgencyLevel !== 'normal' && (
                <div className="absolute top-2 left-2 z-10">
                  {urgencyLevel === 'critical' && (
                    <Badge className="bg-red-500 text-white border-red-600">
                      <Timer className="w-3 h-3 mr-1" />
                      {hoursUntilPayment}시간 남음
                    </Badge>
                  )}
                  {urgencyLevel === 'warning' && status === 'pending' && (
                    <Badge className="bg-orange-500 text-white border-orange-600">
                      <Timer className="w-3 h-3 mr-1" />
                      {hoursUntilPayment}시간 내 결제
                    </Badge>
                  )}
                  {urgencyLevel === 'warning' && status === 'confirmed' && (
                    <Badge className="bg-orange-500 text-white border-orange-600">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      D-{daysUntil}
                    </Badge>
                  )}
                  {urgencyLevel === 'info' && (
                    <Badge className="bg-yellow-500 text-white border-yellow-600">
                      D-{daysUntil}
                    </Badge>
                  )}
                </div>
              )}
              <img src={getImageUrl(booking)} alt={course?.name || booking.trip.product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={e => {
              (e.target as HTMLImageElement).src = '/images/image1.webp';
            }} />
              {/* 상태 배지 오버레이 - 우측에 위치 */}
              <div className="absolute top-2 right-2">
                <Badge className={cn(
                  "shadow-sm border-0",
                  status === 'pending' && "bg-red-500 text-white hover:bg-red-600",
                  status === 'confirmed' && "bg-green-500 text-white hover:bg-green-600",
                  isCompletedTrip && "bg-white text-black hover:bg-gray-50",
                  status === 'cancelled' && "bg-gray-500 text-white hover:bg-gray-600"
                )}>
                  {getBookingStatusText(status)}
                </Badge>
              </div>
            </div>
            
            <div className="flex-1 p-4 md:p-5 space-y-3">
              {/* 헤더 - 상품명 */}
              <div>
                <h3 className="font-semibold text-foreground text-base md:text-lg line-clamp-1">
                  {booking.trip.product.name}
                </h3>
                {course && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">
                    {course.name}
                  </p>}
              </div>
              
              {/* 핵심 정보 그리드 */}
              <div className="grid grid-cols-2 gap-3 py-2">
                <div>
                  <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                    <Calendar className="h-3.5 w-3.5" />
                    <span className="text-xs">출발일</span>
                  </div>
                  <p className="font-medium text-sm">{formatDate(booking.trip.departure_date)}</p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                    <Users className="h-3.5 w-3.5" />
                    <span className="text-xs">참가인원</span>
                  </div>
                  <p className="font-medium text-sm">{booking.num_participants}명</p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                    <MapPin className="h-3.5 w-3.5" />
                    <span className="text-xs">경로</span>
                  </div>
                  <p className="font-medium text-sm line-clamp-1">{getLocationText(booking)}</p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                    <CreditCard className="h-3.5 w-3.5" />
                    <span className="text-xs">결제금액</span>
                  </div>
                  <p className="font-medium text-sm">{Number(booking.total_price).toLocaleString()}원</p>
                </div>
              </div>

              {/* 상세 정보 - 모바일에서는 접기/펼치기 */}
              {isMobile ? <Collapsible open={isExpanded} onOpenChange={() => toggleCardExpansion(booking.booking_id)}>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center justify-between py-2 text-sm text-gray-600">
                      <span>상세 정보 보기</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <Separator className="mb-3" />
                    {renderBookingDetails(booking)}
                  </CollapsibleContent>
                </Collapsible> : <>
                  <Separator />
                  {renderBookingDetails(booking)}
                </>}

              {/* 액션 버튼 영역 */}
              <div className="pt-3 md:pt-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {status === 'pending' && <div className="flex items-center gap-2 text-orange-600">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">결제를 완료해주세요</span>
                    </div>}
                  {status === 'confirmed' && <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">예약이 완료되었습니다</span>
                    </div>}
                  {isCompletedTrip && <div className="flex items-center gap-2 text-gray-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">여행이 완료되었습니다</span>
                    </div>}
                </div>
                
                <Button variant={status === 'pending' ? 'default' : 'ghost'} size="sm" asChild className={cn("group", status === 'pending' && "bg-[#F39800] hover:bg-[#E08700] text-white")}>
                  <Link to={status === 'pending' ? `/payment/${booking.booking_id}` : `/booking/detail/${booking.booking_id}`} className="flex items-center gap-2">
                    {status === 'pending' ? <>
                        <CreditCard className="w-4 h-4" />
                        <span>결제하기</span>
                      </> : <>
                        <span>자세히 보기</span>
                        <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                      </>}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>;
  };

  // 예약 상세 정보 렌더링 함수
  const renderBookingDetails = (booking: BookingWithDetails) => {
    return <div className="space-y-2 text-xs md:text-sm">
        {/* 출발 정보 */}
        {booking.trip.departure_point && <div className="flex items-start gap-2">
            <Bus className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <span className="text-muted-foreground">출발:</span>
              <span className="ml-1">{booking.trip.departure_point}</span>
              {booking.trip.via_points && <span className="text-muted-foreground"> → {booking.trip.via_points}</span>}
            </div>
          </div>}

        {/* 투어 리더 정보 */}
        {booking.trip.tour_leader && <div className="flex items-start gap-2">
            <User className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground mt-0.5" />
            <div>
              <span className="text-muted-foreground">투어 리더:</span>
              <span className="ml-1">{booking.trip.tour_leader}</span>
            </div>
          </div>}

        {/* 기사 정보 */}
        {booking.trip.driver_name && <div className="flex items-start gap-2">
            <Phone className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground mt-0.5" />
            <div>
              <span className="text-muted-foreground">기사:</span>
              <span className="ml-1">{booking.trip.driver_name}</span>
              {booking.trip.driver_phone && <span className="text-muted-foreground"> ({booking.trip.driver_phone})</span>}
            </div>
          </div>}

        {/* 참가자 정보 */}
        {booking.booking_participants.length > 0 && <div className="mt-3">
            <h4 className="font-medium mb-2 text-sm">참가자 정보</h4>
            <div className="grid grid-cols-1 gap-1">
              {booking.booking_participants.map(participant => <div key={participant.id} className="flex items-center gap-2 text-xs md:text-sm">
                  <span className="font-medium">{participant.name}</span>
                  <span className="text-muted-foreground">({participant.phone})</span>
                  {participant.seat && <Badge variant="outline" className="text-xs">
                      좌석 {participant.seat}
                    </Badge>}
                </div>)}
            </div>
          </div>}

        {/* 예약 번호 */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-2">
          <span>예약번호: {booking.order_id || booking.booking_id}</span>
          <span>•</span>
          <span>예약일: {new Date(booking.created_at).toLocaleDateString('ko-KR')}</span>
        </div>
      </div>;
  };
  const renderBookingSection = (title: string, bookings: BookingWithDetails[], icon: React.ReactNode) => {
    if (bookings.length === 0) return null;
    return <div className="space-y-4">
        <div className="flex items-center gap-2 mt-8">
          {icon}
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {title} 
            <Badge variant="secondary" className="ml-2">
              {bookings.length}
            </Badge>
          </h2>
        </div>
        <div className="space-y-3 md:space-y-4">
          {bookings.map(renderBookingCard)}
        </div>
      </div>;
  };
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <Card className="text-center">
              <CardContent className="py-12">
                <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">로그인이 필요합니다</h3>
                <p className="text-muted-foreground mb-6">예약 내역을 확인하려면 로그인해주세요.</p>
                <Button asChild className="bg-[#F39800] hover:bg-[#E08700] text-white">
                  <Link to="/auth">로그인</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>;
  }
  if (isLoading) {
    return <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="space-y-4">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-4 w-1/2" />
              <div className="space-y-4 mt-8">
                {[1, 2, 3].map(i => <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        <Skeleton className="w-24 h-24 rounded-lg" />
                        <div className="flex-1 space-y-3">
                          <div className="flex justify-between">
                            <Skeleton className="h-6 w-2/3" />
                            <Skeleton className="h-6 w-20" />
                          </div>
                          <Skeleton className="h-4 w-full" />
                          <div className="grid grid-cols-2 gap-2">
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-4 w-full" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>;
  }
  if (error) {
    return <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="py-8">
                <ErrorState title="예약 내역을 불러올 수 없습니다" description="일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요." onRetry={() => window.location.reload()} />
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>;
  }
  const {
    pending,
    confirmed,
    completed,
    cancelled
  } = categorizeBookings(bookings || []);
  const hasAnyBookings = bookings && bookings.length > 0;
  
  // 정렬된 예약 목록
  const sortedBookings = bookings ? getSortedBookings(bookings) : [];
  const sortedPending = getSortedBookings(pending);
  const sortedConfirmed = getSortedBookings(confirmed);
  const sortedCompleted = getSortedBookings(completed);
  const sortedCancelled = getSortedBookings(cancelled);
  
  // 긴급 알림 확인
  const urgentPayments = pending.filter(b => getHoursUntilPaymentDeadline(b.created_at) <= 6);
  const nearDepartures = confirmed.filter(b => getDaysUntilDeparture(b.trip.departure_date) <= 3);
  const hasUrgentActions = urgentPayments.length > 0 || nearDepartures.length > 0;
  
  return <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">예약 내역</h1>
                <p className="text-sm md:text-base text-muted-foreground">상태별로 예약 내역을 확인해보세요!</p>
              </div>
              {hasAnyBookings && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      {
                        sortOption === 'urgency' ? '긴급도 순' :
                        sortOption === 'departure' ? '출발일 순' :
                        sortOption === 'booking' ? '예약일 순' :
                        '상태별'
                      }
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleSortChange('urgency')}>
                      <AlertCircle className="w-4 h-4 mr-2" />
                      긴급도 순 (추천)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSortChange('departure')}>
                      <Calendar className="w-4 h-4 mr-2" />
                      출발일 순
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSortChange('booking')}>
                      <Clock3 className="w-4 h-4 mr-2" />
                      예약일 순
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSortChange('status')}>
                      <SortAsc className="w-4 h-4 mr-2" />
                      상태별
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            
            {/* 스마트 알림 배너 */}
            {hasUrgentActions && (
              <Alert className="mb-4 border-orange-200 bg-orange-50">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-sm">
                  <div className="space-y-1">
                    {urgentPayments.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Badge variant="destructive" className="text-xs">
                          긴급
                        </Badge>
                        <span className="text-orange-800">
                          {urgentPayments.length}건의 예약이 6시간 이내 결제가 필요합니다.
                        </span>
                      </div>
                    )}
                    {nearDepartures.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-orange-500 text-white text-xs">
                          임박
                        </Badge>
                        <span className="text-orange-800">
                          {nearDepartures.length}건의 여행이 3일 이내 출발합니다.
                        </span>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 메인 콘텐츠 영역 */}
            <div className="md:col-span-2">
              {!hasAnyBookings ? <Card>
                  <CardContent className="py-8">
                    <EmptyState icon={ShoppingBag} title="예약 내역이 없습니다" description="한국의 아름다운 둘레길을 트레킹하며 새로운 모험을 시작해보세요!" action={<Button asChild className="bg-[#F39800] hover:bg-[#E08700] text-white">
                          <Link to="/courses">
                            <TrendingUp className="w-4 h-4 mr-2" />
                            트레킹 코스 둘러보기
                          </Link>
                        </Button>} />
                  </CardContent>
                </Card> : <>
                  {/* 탭 네비게이션 - 데스크탑에서만 표시 */}
                  {!isMobile && <Tabs defaultValue="all" className="mb-6">
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="all">
                      전체 ({bookings?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="pending">
                      <span className="relative">
                        결제 대기
                        {pending.length > 0 && <Badge variant="destructive" className="absolute -top-2 left-full ml-1 h-4 w-4 p-0 flex items-center justify-center text-xs">
                            {pending.length}
                          </Badge>}
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="confirmed">
                      예정된 여행 ({confirmed.length})
                    </TabsTrigger>
                    <TabsTrigger value="completed">
                      완료된 여행 ({completed.length})
                    </TabsTrigger>
                    <TabsTrigger value="cancelled">
                      취소된 여행 ({cancelled.length})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="all" className="space-y-4">
                    {sortedBookings.length === 0 ? <Card>
                        <CardContent className="py-8">
                          <EmptyState icon={ShoppingBag} title="예약 내역이 없습니다" description="새로운 트레킹 여행을 시작해보세요!" />
                        </CardContent>
                      </Card> : sortedBookings.map(renderBookingCard)}
                  </TabsContent>
                  
                  <TabsContent value="pending" className="space-y-4">
                    {pending.length === 0 ? <Card>
                        <CardContent className="py-8">
                          <EmptyState icon={CreditCard} title="결제 대기 중인 예약이 없습니다" description="예약 후 결제를 완료하지 않은 내역이 없습니다." />
                        </CardContent>
                      </Card> : sortedPending.map(renderBookingCard)}
                  </TabsContent>
                  
                  <TabsContent value="confirmed" className="space-y-4">
                    {confirmed.length === 0 ? <Card>
                        <CardContent className="py-8">
                          <EmptyState icon={Calendar} title="예정된 여행이 없습니다" description="다가오는 트레킹 일정이 없습니다. 새로운 모험을 계획해보세요!" action={<Button asChild variant="outline">
                                <Link to="/courses">
                                  코스 둘러보기
                                </Link>
                              </Button>} />
                        </CardContent>
                      </Card> : sortedConfirmed.map(renderBookingCard)}
                  </TabsContent>
                  
                  <TabsContent value="completed" className="space-y-4">
                    {completed.length === 0 ? <Card>
                        <CardContent className="py-8">
                          <EmptyState icon={CheckCircle2} title="완료된 여행이 없습니다" description="아직 완료된 트레킹 여행이 없습니다." />
                        </CardContent>
                      </Card> : sortedCompleted.map(renderBookingCard)}
                  </TabsContent>
                  
                  <TabsContent value="cancelled" className="space-y-4">
                    {cancelled.length === 0 ? <Card>
                        <CardContent className="py-8">
                          <EmptyState icon={X} title="취소된 여행이 없습니다" description="취소된 여행 내역이 없습니다." />
                        </CardContent>
                      </Card> : sortedCancelled.map(renderBookingCard)}
                  </TabsContent>
                    </Tabs>}

                  {/* 모바일 뷰 - 순차적 표시 */}
                  {isMobile && <div className="space-y-6">
                      {pending.length > 0 && <div className="bg-orange-50 rounded-lg p-3 mb-4">
                          <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            결제 대기 중인 예약이 {pending.length}건 있습니다.
                          </p>
                        </div>}
                      
                      {renderBookingSection("결제 대기 중", sortedPending, <CreditCard className="w-5 h-5 text-orange-500" />)}
                      {renderBookingSection("예정된 여행", sortedConfirmed, <CheckCircle2 className="w-5 h-5 text-green-500" />)}
                      {renderBookingSection("완료된 여행", sortedCompleted, <CheckCircle2 className="w-5 h-5 text-gray-500" />)}
                      {renderBookingSection("취소된 여행", sortedCancelled, <X className="w-5 h-5 text-red-500" />)}
                    </div>}
                </>}
            </div>

            {/* 사이드바 - 데스크탑에서만 표시 */}
            {!isMobile && hasAnyBookings && <div className="space-y-6">
                <div className="sticky top-4 z-10">
                  {/* 통합된 예약 현황 및 도우미 카드 */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Package2 className="w-5 h-5 text-[#F39800]" />
                        예약 현황
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">전체 예약</span>
                        <Badge variant="secondary">{bookings?.length || 0}건</Badge>
                      </div>
                      {pending.length > 0 && <div className="flex justify-between items-center">
                          <span className="text-sm text-orange-600">결제 대기</span>
                          <Badge variant="destructive">{pending.length}건</Badge>
                        </div>}
                      {confirmed.length > 0 && <div className="flex justify-between items-center">
                          <span className="text-sm text-green-600">예정된 여행</span>
                          <Badge variant="default" className="bg-green-100 text-green-700 hover:bg-green-200">
                            {confirmed.length}건
                          </Badge>
                        </div>}
                      {completed.length > 0 && <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">완료된 여행</span>
                          <Badge variant="outline">{completed.length}건</Badge>
                        </div>}
                      
                      <Separator className="my-4" />
                      
                      {/* 예약 관리 도우미 섹션 - 리뉴얼 */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-base flex items-center gap-2">
                          <HelpCircle className="w-4 h-4 text-[#F39800]" />
                          예약 관리 도우미
                        </h4>
                        
                        {/* 결제 대기 알림 - 24시간 유효기간 강조 */}
                        {pending.length > 0 && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <Timer className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                              <div className="space-y-1 flex-1">
                                <p className="text-sm font-medium text-orange-900">
                                  결제 대기 중인 예약이 있습니다
                                </p>
                                <p className="text-xs text-orange-700">
                                  예약 후 <span className="font-semibold">24시간 이내</span>에 결제를 완료하셔야 합니다.
                                </p>
                                <p className="text-xs text-orange-600">
                                  기간 내 결제 시 선택하신 좌석이 유지되며, 24시간 경과 시 자동 취소됩니다.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 빠른 액션 버튼들 */}
                        <div className="grid grid-cols-2 gap-2">
                          <Button 
                            asChild 
                            variant="outline" 
                            size="sm"
                            className="h-auto flex flex-col items-center py-3 hover:bg-gray-50 hover:border-[#F39800]"
                          >
                            <Link to="/faq">
                              <MessageSquare className="w-4 h-4 mb-1 text-[#F39800]" />
                              <span className="text-xs">자주묻는질문</span>
                            </Link>
                          </Button>
                          <Button 
                            asChild 
                            variant="outline" 
                            size="sm"
                            className="h-auto flex flex-col items-center py-3 hover:bg-gray-50 hover:border-[#F39800]"
                          >
                            <Link to="/refund-policy">
                              <Shield className="w-4 h-4 mb-1 text-[#F39800]" />
                              <span className="text-xs">환불정책</span>
                            </Link>
                          </Button>
                        </div>
                        
                        {/* 예약 상태별 안내 - 아이콘 추가 */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-sm flex items-center gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5 text-gray-500" />
                            예약 상태 안내
                          </h5>
                          <div className="space-y-2 text-xs">
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive" className="h-5 px-2 text-xs">
                                <Timer className="w-3 h-3 mr-1" />
                                결제대기
                              </Badge>
                              <span className="text-muted-foreground">24시간 내 결제 필요</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="h-5 px-2 text-xs bg-green-500 text-white hover:bg-green-600">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                예약완료
                              </Badge>
                              <span className="text-muted-foreground">결제 완료</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="h-5 px-2 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                여행완료
                              </Badge>
                              <span className="text-muted-foreground">리뷰 작성 가능</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* 환불 정책 - 시각적 개선 */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-sm flex items-center gap-1.5">
                            <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
                            취소 및 환불 정책
                          </h5>
                          <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">출발 4일 전까지</span>
                              <Badge className="bg-green-100 text-green-700 border-green-200">100%</Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">출발 3~1일 전 오전</span>
                              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">70%</Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 text-[11px]">(오전 12시 이전)</span>
                              <span></span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">출발 1일 전 오후</span>
                              <Badge className="bg-red-100 text-red-700 border-red-200">0%</Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 text-[11px]">(오후 12시 이후)</span>
                              <span></span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">출발 당일</span>
                              <Badge className="bg-red-100 text-red-700 border-red-200">0%</Badge>
                            </div>
                          </div>
                          <p className="text-[10px] text-muted-foreground leading-relaxed">
                            * 천재지변 또는 최소인원 미달 시 100% 환불
                          </p>
                        </div>

                        {/* 자주 묻는 질문 미리보기 */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-sm flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-gray-500" />
                            예약 관련 FAQ
                          </h5>
                          <div className="space-y-2">
                            <details className="group">
                              <summary className="cursor-pointer text-xs text-gray-700 hover:text-[#F39800] transition-colors flex items-center justify-between">
                                <span>예약 후 언제까지 결제해야 하나요?</span>
                                <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                              </summary>
                              <p className="mt-2 text-[11px] text-gray-600 pl-3 border-l-2 border-gray-200">
                                예약 후 24시간 이내에 결제를 완료하셔야 합니다. 기간 내 미결제 시 예약이 자동 취소되며, 선택하신 좌석도 취소됩니다.
                              </p>
                            </details>
                            <details className="group">
                              <summary className="cursor-pointer text-xs text-gray-700 hover:text-[#F39800] transition-colors flex items-center justify-between">
                                <span>예약 변경이나 취소는 어떻게 하나요?</span>
                                <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                              </summary>
                              <p className="mt-2 text-[11px] text-gray-600 pl-3 border-l-2 border-gray-200">
                                예약 상세 페이지에서 변경 또는 취소하실 수 있습니다. 
                                <br/>• <strong>예약 수정</strong>: 출발 24시간 전까지 가능
                                <br/>• <strong>예약 취소</strong>: 당일까지 가능 (환불 금액은 시기별로 다름)
                              </p>
                            </details>
                            <details className="group">
                              <summary className="cursor-pointer text-xs text-gray-700 hover:text-[#F39800] transition-colors flex items-center justify-between">
                                <span>최소 출발 인원이 모이지 않으면?</span>
                                <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                              </summary>
                              <p className="mt-2 text-[11px] text-gray-600 pl-3 border-l-2 border-gray-200">
                                최소 인원(15명) 미달 시 출발 2일 전 안내드리며, 전액 환불 또는 다른 일정으로 변경 가능합니다.
                              </p>
                            </details>
                          </div>
                        </div>

                        <Separator />

                        <div className="space-y-3">
                          <Button asChild variant="default" size="sm" className="w-full bg-[#F39800] hover:bg-[#E08700]">
                            <Link to="/courses">
                              <TrendingUp className="w-4 h-4 mr-2" />
                              새로운 코스 둘러보기
                            </Link>
                          </Button>
                          <Button asChild variant="outline" size="sm" className="w-full">
                            <a href="tel:010-3741-2556">
                              <Phone className="w-4 h-4 mr-2" />
                              고객센터 전화하기
                            </a>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>}
          </div>
        </div>
      </div>
      <Footer />
    </div>;
};
export default BookingHistory;